import React from "react";
import { useNavigate } from "react-router-dom";
import {
  ShieldAlert,
  AlertTriangle,
  Waves,
  Wrench,
  Anchor,
  Drill,
  Compass,
  Activity,
  Database,
  UserCheck,
  Siren,
  ChevronLeft,
} from "lucide-react";

// ============================================================================
// Safety Center — accessible at /safety (and the legacy /safety-center alias).
// Read-only. Static disclaimer page. No data, no inputs, no formulas.
// ============================================================================
const SECTIONS = [
  {
    id: "general",
    title: "General Calculations",
    icon: ShieldAlert,
    body:
      "RigSentinel is a calculation-support and reference tool. Every calculation, conversion, and worksheet must be independently verified by qualified personnel against approved company procedures, manufacturer data, well-specific data, and applicable regulations before any operational use. Always check input values, units, formulas, assumptions, intermediate steps, and final results. Do not treat any output as a directive.",
  },
  {
    id: "well-control",
    title: "Well Control",
    icon: AlertTriangle,
    body:
      "Well-control calculators (kill sheet drafts, MAASP, pump pressure schedules, circulating pressures, kick tolerance, leak-off / formation strength, slow-circulation rates, kill weights, choke schedules) provide computational support only. They are NOT a live well-control instruction system. Live well-control decisions must always be made by certified well-control personnel using approved company procedures, the rig's IWCF/IADC-aligned well-control manual, regulatory requirements, and current well data. Never operate the choke, pumps, BOP, diverter, or any well-control equipment based solely on output from this tool.",
  },
  {
    id: "subsea",
    title: "Subsea Operations",
    icon: Waves,
    body:
      "Subsea well-control and subsea-stack calculations require rig-specific subsea stack data, marine riser geometry, choke/kill line lengths and IDs, control system response, hydrate-management procedures, mudline-suspension data, and qualified subsea well-control review. Riser margins, gas-in-riser effects, choke-line friction at slow circulation, deepwater hydrate considerations, and disconnect/hang-off procedures are all rig-, vessel-, and well-specific. The values produced by this tool are not a substitute for the rig's approved subsea well-control program or OEM data.",
  },
  {
    id: "bop",
    title: "BOP and Well-Control Equipment",
    icon: ShieldAlert,
    body:
      "Any reference data, capacities, working pressures, ratings, response volumes, accumulator volumes, regulator settings, or function/response times displayed in this tool are illustrative reference numbers only. Always use the rig-, well-, and equipment-specific OEM datasheets, the rig's current Subsea/Surface BOP test records, API/IADC/IOGP standards, regulatory authority requirements, and the operator's approved well-control program for any operational decision. Never use this tool as a substitute for the rig's certified BOP test sheets, BOP control-system documentation, or function tests.",
  },
  {
    id: "tubulars",
    title: "Tubulars and Mechanical Calculations",
    icon: Wrench,
    body:
      "Tubular calculations (burst, collapse, tension, triaxial, torque & drag, buckling, makeup torque, hookload, weight-in-air / weight-in-mud, slip crushing, casing/tubing string design) are simplified deterministic models. Field application of these values requires full design verification using OEM tubular data sheets, current API/ISO design standards, the operator's casing/tubing design basis, validated software, and a qualified tubular/well engineer. Do not use these outputs as a substitute for a full casing/tubing design review.",
  },
  {
    id: "lifting",
    title: "Lifting Operations",
    icon: Anchor,
    body:
      "Lifting-related calculations (sling loads, sling angle factors, hook loads, padeye capacities, COG estimates, dynamic amplification factors, tugger pulls) are simplified guidelines for planning support. They do not replace a competent lifting plan, certified rigging, validated lift studies, manufacturer certificates, current lifting register, the rig's lifting procedures, or the supervision of a competent lifting/rigging supervisor. Never perform a lift solely on the basis of values produced by this tool.",
  },
  {
    id: "stuck-pipe",
    title: "Stuck Pipe and Fishing",
    icon: Drill,
    body:
      "Stuck-pipe and fishing calculations (free-point, stretch, neutral point, jar impact, overpull limits, free-pipe estimation, back-off torque) are approximate and depend strongly on accurate string composition, true geometry, mud properties, and friction assumptions. They must be cross-checked against the operator's stuck-pipe and fishing procedures, the rig's BHA records, the actual survey, and qualified drilling/fishing supervision. Never apply pull, torque, jarring, back-off, or chemical/mechanical remediation actions based solely on this tool.",
  },
  {
    id: "directional",
    title: "Directional Drilling",
    icon: Compass,
    body:
      "Directional, survey, and trajectory calculations (DLS, build/turn rates, tortuosity, vertical section, closure, minimum-curvature/balanced-tangential survey methods, anti-collision separation factors) shown here are simplified deterministic implementations. Anti-collision, well-placement, and survey decisions must use the operator's certified directional/survey software, validated reference well data, qualified directional drilling personnel, and the company's anti-collision policy. This tool is not a survey-management or anti-collision system.",
  },
  {
    id: "production",
    title: "Production and Reservoir",
    icon: Activity,
    body:
      "Production, reservoir, fluid-property, and inflow/outflow calculations are simplified, single-point, deterministic models. Real reservoir and production engineering requires PVT-validated fluid data, well test data, full nodal-analysis software, calibrated correlations, and qualified production/reservoir engineering review. Do not use this tool to set production rates, choke positions, allocation factors, lift-gas rates, or to make any reservoir management decision.",
  },
  {
    id: "data",
    title: "Data, References, and Copyright",
    icon: Database,
    body:
      "Reference values, capacities, density tables, friction factors, additive guidance, conversion factors, and rules-of-thumb included in this tool are general industry references intended for orientation only. They are not a substitute for OEM data, vendor product sheets, the operator's approved data set, or current regulatory references. RigSentinel does not warrant the accuracy, completeness, or fitness for purpose of any reference data. All trademarks, standards, and references remain the property of their respective owners.",
  },
  {
    id: "user-responsibility",
    title: "User Responsibility",
    icon: UserCheck,
    body:
      "By using RigSentinel the user accepts full responsibility for verifying every input, formula, unit, assumption, intermediate value, and final result before any operational, training, reporting, or commercial use. The user is responsible for ensuring the tool is suitable for the intended task, for compliance with company procedures and applicable regulations, and for the consequences of any decision taken using this tool. The developers, distributors, and contributors of RigSentinel accept no liability for any direct, indirect, incidental, or consequential loss or damage of any kind arising from use of this tool.",
  },
  {
    id: "emergency",
    title: "Emergency Use Warning",
    icon: Siren,
    body:
      "RigSentinel must NEVER be used as the primary decision-making system during a kick, well-control event, blowout, loss of well control, stuck-pipe emergency, lifting incident, fire, gas release, evacuation, or any other emergency. During emergencies, follow the rig's emergency response procedures, well-control manual, the OIM/Toolpusher/Driller's instructions, certified personnel, and the operator's incident-command system. This tool is a reference-only aid for planning, training, documentation, and qualified review.",
  },
];

export default function Safety() {
  const navigate = useNavigate();
  return (
    <div className="flex flex-col gap-5 bg-grain pb-2" data-testid="safety-center-page">
      <section className="pt-1">
        <div className="font-mono-rs text-[10px] tracking-[0.22em] uppercase text-[var(--rs-amber)]">
          RigSentinel · Safety
        </div>
        <h1
          className="font-heading font-black text-3xl tracking-tight mt-1"
          data-testid="safety-center-title"
        >
          Safety Center
        </h1>
        <p className="text-sm text-[var(--rs-text-2)] mt-1.5 leading-relaxed">
          Read these disclaimers before relying on any calculation, conversion,
          worksheet, or reference value produced by this tool. RigSentinel
          is a calculation-support and reference application — not an
          operational, control, or emergency-response system.
        </p>
      </section>

      {/* Top red banner */}
      <div
        data-testid="safety-top-banner"
        className="card-rs p-4 flex gap-3"
        style={{
          background: "rgba(244,67,54,0.10)",
          borderColor: "#C62828",
        }}
      >
        <AlertTriangle
          className="w-5 h-5 mt-0.5 shrink-0"
          style={{ color: "var(--rs-danger)" }}
        />
        <div>
          <div
            className="text-sm font-bold"
            style={{ color: "var(--rs-danger)" }}
          >
            Reference-only. Not for live well-control or emergency use.
          </div>
          <p className="text-xs mt-1 leading-snug text-[#FCA5A5]">
            All outputs require independent verification by competent personnel
            using approved procedures, manufacturer data, current well data, and
            regulatory requirements.
          </p>
        </div>
      </div>

      {/* Section cards */}
      <section className="flex flex-col gap-3">
        {SECTIONS.map(({ id, title, icon: Icon, body }) => (
          <article
            key={id}
            id={`safety-${id}`}
            data-testid={`safety-section-${id}`}
            className="card-rs p-4"
          >
            <div className="flex items-center gap-2.5 mb-2">
              <Icon
                className="w-4 h-4 shrink-0"
                style={{ color: "var(--rs-amber)" }}
              />
              <h2 className="font-heading font-black text-base tracking-tight">
                {title}
              </h2>
            </div>
            <p className="text-xs text-[var(--rs-text-2)] leading-relaxed">
              {body}
            </p>
          </article>
        ))}
      </section>

      {/* Bottom acknowledgement card */}
      <div
        data-testid="safety-ack-card"
        className="card-rs p-4"
        style={{ borderColor: "var(--rs-amber)" }}
      >
        <div className="label-up text-[var(--rs-amber)] mb-1.5">
          Acknowledgement
        </div>
        <p className="text-xs text-[var(--rs-text-2)] leading-relaxed">
          Continued use of RigSentinel constitutes acknowledgement of
          these disclaimers. Outputs are calculation support only. The user
          remains responsible for all operational, safety, regulatory, and
          commercial consequences.
        </p>
        <button
          data-testid="safety-back-home-btn"
          onClick={() => navigate("/home")}
          className="btn-ghost h-10 px-3 rounded-md inline-flex items-center gap-2 text-xs label-up mt-3"
        >
          <ChevronLeft className="w-4 h-4" /> Back to Home
        </button>
      </div>
    </div>
  );
}
